"use client";

import { useEffect, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Separator } from "@/components/ui/separator";
import { MapPin, Mail, Phone, ArrowRight, Menu, X, Facebook, Instagram } from "lucide-react";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  if (loading) {
    return <div className="flex gap-1">
        <div className="h-9 w-16 bg-muted/20 animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted/20 animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted/20 animate-pulse rounded-md" />
      </div>;
  }
  return <>
      {}
      <NavigationMenu viewport={false} className="hidden lg:flex">
        <NavigationMenuList>
          {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
              <NavigationMenuLink asChild>
                <Link to={item.href || "#"} onClick={() => setActiveIndex(i)} className="group relative inline-flex h-9 items-center justify-center px-4 py-2 text-sm font-semibold transition-all duration-[var(--duration-medium)] outline-none rounded-md text-black hover:bg-transparent focus:bg-transparent">
                  {item.label}
                  {}
                  <span style={{
                backgroundColor: "#a03223"
              }} className={`absolute bottom-1 left-4 right-4 h-[2px] transition-transform duration-[var(--duration-medium)] ease-[var(--ease-smooth)] origin-left ${activeIndex === i ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}`} />
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>)}
        </NavigationMenuList>
      </NavigationMenu>

      {}
      <Button variant="ghost" size="icon" className="lg:hidden hover:bg-white/10 focus:bg-white/10" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu">
        {mobileMenuOpen ? <X className="size-6 text-[#c39555]" /> : <Menu className="size-6 text-[#c39555]" />}
      </Button>

      {}
      {mobileMenuOpen && <div className="absolute top-full left-0 right-0 bg-light-background border-t shadow-medium lg:hidden z-[110]">
          <nav className="flex flex-col p-4 gap-2">
            {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className={`px-4 py-3 text-sm font-semibold transition-colors duration-[var(--duration-fast)] text-black`} onClick={() => {
          setActiveIndex(i);
          setMobileMenuOpen(false);
        }}>
                {item.label}
              </Link>)}
          </nav>
        </div>}
    </>;
}
export default function TopBarMainNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="top-bar-main-navigation" className="w-full animate-fade-in-up">
      {}
      <div className="w-full bg-dark-background min-h-[48px]">
        <div className="max-w-7xl mx-auto h-full px-6">
          <div className="flex items-center justify-end h-[48px] text-foreground-dark text-sm font-sans">
            <div className="flex items-center gap-4">
              <div className="hidden md:flex items-center gap-2">
                <MapPin className="size-4 text-white" />
                <span className="text-white">Aisopou 17, Agios Nikolaos, 3100, Limassol</span>
              </div>
              <Separator orientation="vertical" className="h-4 bg-muted-dark-background hidden md:block" />
              <div className="hidden md:flex items-center gap-2">
                <Mail className="size-4 text-white" />
                <span className="text-white">ks@theksway.com</span>
              </div>
              <Separator orientation="vertical" className="h-4 bg-muted-dark-background hidden md:block" />
              <div className="flex items-center gap-3">
                <Link to="https://www.facebook.com/KSLuxuryRenovations" className="text-foreground-dark hover:text-foreground-dark transition-colors" aria-label="Facebook">
                  <Facebook className="size-4 text-white" />
                </Link>
                <Link to="https://www.instagram.com/the_ks_way/" className="text-foreground-dark hover:text-foreground-dark transition-colors" aria-label="Instagram">
                  <Instagram className="size-4 text-white" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="w-full bg-light-background sticky top-0 z-40 shadow-soft transition-all duration-[var(--duration-medium)] h-[90px]">
        <div className="max-w-7xl mx-auto h-full px-6">
          <div className="flex items-center justify-between h-full relative">
            <Link to="/" className="hover:opacity-80 transition-opacity">
              <img src="https://ksluxury.cloudtech.com.cy/wp-content/uploads/2026/02/KS-Logo-250px.png" alt="KS Luxury" className="w-[227px] h-[90px] object-contain" />
            </Link>

            <div className="flex items-center gap-6">
              <MenuProvider menu_id="13">
                <HeaderMenuConsumer />
              </MenuProvider>

              {}
              <div className="hidden lg:flex items-center gap-3">
                <Button asChild className="bg-accent text-white hover:bg-accent-hover rounded-full px-6 py-3 h-auto font-semibold">
                  <Link to="tel:97673308" className="flex items-center gap-2">
                    <Phone className="size-4 text-white" />
                    <span className="text-white">97 673 308</span>
                  </Link>
                </Button>

                <Button asChild className="bg-accent text-white hover:bg-accent-hover rounded-full px-6 py-3 h-auto font-semibold">
                  <Link to="https://ksluxury.cloudtech.com.cy/get-a-quote/" className="flex items-center gap-2">
                    <span className="text-white">Get A Quote</span>
                    <ArrowRight className="size-4 text-white" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>;
}